// Implements: F-IDN-027
/**
 * "¿Quitar a … de la organización?" (v3 `openRemovePerson`): who receives the person's mockups,
 * then "Reasignar y quitar". The confirm button is busy while the writes run, so a double press
 * sends nothing twice; a refusal is shown inside the dialog, which stays open.
 *
 * Owner 2026-10-19: the recipient list holds only the people who can receive EVERY mockup of this
 * organization the person owns (the server's `reassign-candidates`, the same rule the all-or-nothing
 * reassignment refuses with), the caller marked "(tú)" and the first one preselected. While that
 * answer is on its way nothing can be confirmed. Four outcomes:
 * - the person owns nothing here: "No tiene mockups…" and a plain "Quitar" that moves nothing;
 * - somebody can receive them: "Reasignar sus N mockups a…" and "Reasignar y quitar";
 * - nobody can: the dialog says why and what to do, and offers no removal;
 * - the answer cannot be read: the reason, and no removal.
 * Only the person's mockups of this organization move; their personal ones stay with them.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Select } from "../../../shared/ui/atoms/Select";
import { Field, hintId } from "../../../shared/ui/molecules/Field";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { UserSummary } from "../../../types/api";
import type { ReassignCandidates } from "../../../types/organization";
import styles from "./Organization.module.css";

export interface RemovePersonDialogProps {
  readonly person: UserSummary;
  /** Who can receive the person's mockups; `undefined` while the answer is on its way. */
  readonly candidates: ReassignCandidates | undefined;
  /** Why the candidates could not be read, already in the person's words, or `null`. */
  readonly candidatesProblem: string | null;
  readonly currentUserId: string;
  readonly pending: boolean;
  /** The refusal to show, already in the person's words, or `null`. */
  readonly problem: string | null;
  /** The chosen recipient, or `null` when the person owns nothing here and nothing moves. */
  readonly onConfirm: (recipientId: string | null) => void;
  readonly onCancel: () => void;
}

const RECIPIENT_ID = "remove-person-recipient";

export function RemovePersonDialog({
  person,
  candidates,
  candidatesProblem,
  currentUserId,
  pending,
  problem,
  onConfirm,
  onCancel,
}: RemovePersonDialogProps): ReactElement {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string | null>(null);
  const offered = candidates?.candidates ?? [];
  // A choice that left the list (a re-read) falls back to the first person offered.
  const recipient = offered.some((user) => user.id === chosen) ? (chosen ?? "") : (offered[0]?.id ?? "");
  const nothingToMove = candidates?.owned === 0;
  const ready = candidates !== undefined && candidatesProblem === null && (nothingToMove || recipient !== "");

  function body(): ReactElement | null {
    if (candidatesProblem !== null) return null;
    if (candidates === undefined) {
      return (
        <p className={styles.status} role="status">
          {t("organization.remove.loading")}
        </p>
      );
    }
    if (nothingToMove) return <p className={styles.status}>{t("organization.remove.nothing")}</p>;
    if (offered.length === 0) return <p className={styles.notice}>{t("organization.remove.nobody")}</p>;
    return (
      <Field
        id={RECIPIENT_ID}
        label={t("organization.remove.recipient", { count: candidates.owned })}
        hint={t("organization.remove.recipientHelp")}
      >
        <Select
          id={RECIPIENT_ID}
          value={recipient}
          describedBy={hintId(RECIPIENT_ID)}
          options={offered.map((user) => ({
            value: user.id,
            label: user.id === currentUserId ? `${user.full_name} ${t("organization.you")}` : user.full_name,
          }))}
          onChange={setChosen}
        />
      </Field>
    );
  }

  const shownProblem = problem ?? candidatesProblem;
  return (
    <Dialog
      open
      title={t("organization.remove.title", { name: person.full_name })}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button
            variant="danger"
            loading={pending}
            disabled={!ready}
            onClick={() => {
              if (!pending && ready) onConfirm(nothingToMove ? null : recipient);
            }}
          >
            {nothingToMove ? t("organization.remove.confirmPlain") : t("organization.remove.confirm")}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.status}>{t("organization.remove.body")}</p>
        {body()}
        {shownProblem === null ? null : (
          <p className={styles.problem} role="alert">
            {shownProblem}
          </p>
        )}
      </div>
    </Dialog>
  );
}
